/**
 * logos.ts — central registry for every Coravu logo asset. The brand
 * page's LogoOverlay reads from this registry and renders each entry
 * with download buttons + usage notes. Future refactor: every place in
 * the app that draws a logo should import its SVG from here (via a
 * shared `<Logo id="..." />` component) so swapping a logo in the
 * registry propagates everywhere.
 *
 * Placeholders are generated inline (sun mark + wordmark variants).
 * Replace `svg` strings in this file to swap real artwork in.
 */

export type LogoCategory = "Wordmark" | "Mark" | "Favicon" | "Social" | "App Icon" | "Email";

export type LogoBackground = "transparent" | "light" | "dark" | "any";

export type LogoPane = "General" | "Student" | "Client" | "Admin";

export type LogoVariant = {
  /** Display label, e.g., "SVG", "PNG 32×32". */
  label: string;
  /** Output format. */
  format: "svg" | "png";
  /** For PNG, the export pixel size. For SVG, ignored. */
  pxSize?: number;
  /** Suggested filename (without extension). */
  filename: string;
};

export type Logo = {
  id: string;
  name: string;
  category: LogoCategory;
  background: LogoBackground;
  /** Where this logo is used in the product. */
  usedIn: LogoPane[];
  /** One-line description of where/how it appears. */
  description: string;
  /** Display dimensions for the preview tile (aspect ratio hint). */
  aspect: "square" | "wide" | "social";
  /** Raw SVG markup. */
  svg: string;
  /** Available downloads. Always includes SVG; rasters generated from SVG. */
  variants: LogoVariant[];
};

/* ─────────────────────── Mark geometry (single source) ─────────────────────── */

/**
 * The Coravu sun mark — the ONE geometry definition shared by every render:
 * the live `<Logo>` component (react-native-svg), the animated brand
 * entrances/exits, and the SVG-string factories below that feed `/brand`'s
 * LogoOverlay + the downloadable assets. Authored in a 34×34 viewBox (the
 * proportions every shell already used). Change a radius/ray here and it
 * propagates to the app AND the reference page — they can't drift.
 */
export const MARK_GEOMETRY = {
  viewBox: 34,
  center: 17,
  /** Rounded-tile corner radius for the chrome-tile variant (shell topbars). */
  tileRadius: 7,
  ringOuter: { r: 9.5, strokeWidth: 0.7 },
  ringInner: { r: 5.5, strokeWidth: 1.2 },
  dot: { r: 1.8 },
  ray: {
    strokeWidth: 0.7,
    /** [x1, y1, x2, y2] for the 4 cardinal rays (top, bottom, left, right). */
    lines: [
      [17, 5, 17, 8.5],
      [17, 25.5, 17, 29],
      [5, 17, 8.5, 17],
      [25.5, 17, 29, 17],
    ] as ReadonlyArray<readonly [number, number, number, number]>,
  },
  /** The 4 diagonal rays — only the marketing-nav "full" sunburst uses these,
   *  at a slightly finer stroke than the cardinal rays. */
  rayDiagonal: {
    strokeWidth: 0.6,
    lines: [
      [8.5, 8.5, 10.8, 10.8],
      [23.2, 23.2, 25.5, 25.5],
      [25.5, 8.5, 23.2, 10.8],
      [10.8, 23.2, 8.5, 25.5],
    ] as ReadonlyArray<readonly [number, number, number, number]>,
  },
} as const;

/* ─────────────────────── SVG factories ─────────────────────── */

/** Inner shapes of the sun mark at the 34×34 viewBox — reused by the
 *  standalone mark svg and the lockup (embedded in a scaled group). */
function sunMarkShapes(opts: { ringOuter: string; ringInner: string; dot: string; rays: boolean }): string {
  const g = MARK_GEOMETRY;
  const c = g.center;
  const rayLines = opts.rays
    ? g.ray.lines
        .map(
          ([x1, y1, x2, y2]) =>
            `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${opts.ringOuter}" stroke-width="${g.ray.strokeWidth}"/>`,
        )
        .join("")
    : "";
  return (
    `<circle cx="${c}" cy="${c}" r="${g.ringOuter.r}" fill="none" stroke="${opts.ringOuter}" stroke-width="${g.ringOuter.strokeWidth}"/>` +
    `<circle cx="${c}" cy="${c}" r="${g.ringInner.r}" fill="none" stroke="${opts.ringInner}" stroke-width="${g.ringInner.strokeWidth}"/>` +
    `<circle cx="${c}" cy="${c}" r="${g.dot.r}" fill="${opts.dot}"/>` +
    rayLines
  );
}

function sunMarkSvg(opts: {
  ringOuter?: string;
  ringInner?: string;
  dot?: string;
  bg?: string;
  rays?: boolean;
} = {}): string {
  const size = MARK_GEOMETRY.viewBox;
  const ringOuter = opts.ringOuter ?? "#8FBB8A";
  const ringInner = opts.ringInner ?? "#B5873A";
  const dot = opts.dot ?? "#B5873A";
  const rays = opts.rays ?? true;
  const bgRect = opts.bg ? `<rect width="${size}" height="${size}" fill="${opts.bg}"/>` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">${bgRect}${sunMarkShapes({ ringOuter, ringInner, dot, rays })}</svg>`;
}

function wordmarkSvg(opts: {
  text?: string;
  accent?: string;
  bg?: string;
} = {}): string {
  const text = opts.text ?? "#F7F4EE";
  const accent = opts.accent ?? "#B5873A";
  const bg = opts.bg ? `<rect width="320" height="64" fill="${opts.bg}"/>` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 64" width="320" height="64">${bg}<g font-family="Georgia, 'Cormorant Garamond', serif" font-size="40" letter-spacing="8" font-weight="500"><text x="22" y="44" fill="${text}">COR</text><text x="143" y="44" fill="${accent}">A</text><text x="178" y="44" fill="${text}">VU</text></g></svg>`;
}

function lockupSvg(opts: { text?: string; accent?: string; ringOuter?: string; bg?: string } = {}): string {
  // Sun mark + wordmark side by side. The mark uses the shared
  // MARK_GEOMETRY (scaled from its 34 viewBox into the 64px lockup height)
  // so it matches the live app mark, rays and all.
  const text = opts.text ?? "#F7F4EE";
  const accent = opts.accent ?? "#B5873A";
  const ringOuter = opts.ringOuter ?? "#8FBB8A";
  const bg = opts.bg ? `<rect width="400" height="64" fill="${opts.bg}"/>` : "";
  const scale = 64 / MARK_GEOMETRY.viewBox;
  const mark = `<g transform="scale(${scale})">${sunMarkShapes({ ringOuter, ringInner: accent, dot: accent, rays: true })}</g>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 64" width="400" height="64">${bg}${mark}<g font-family="Georgia, 'Cormorant Garamond', serif" font-size="40" letter-spacing="8" font-weight="500"><text x="82" y="44" fill="${text}">COR</text><text x="203" y="44" fill="${accent}">A</text><text x="238" y="44" fill="${text}">VU</text></g></svg>`;
}

function ogImageSvg(opts: { bg?: string; text?: string; accent?: string; tagline?: string } = {}): string {
  const bg = opts.bg ?? "#131e12";
  const text = opts.text ?? "#F7F4EE";
  const accent = opts.accent ?? "#B5873A";
  const tagline = opts.tagline ?? "Win, don't apply.";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 630" width="1200" height="630"><rect width="1200" height="630" fill="${bg}"/><g transform="translate(600 0)"><circle cx="0" cy="170" r="58" fill="none" stroke="#8FBB8A" stroke-width="1.3"/><circle cx="0" cy="170" r="36" fill="none" stroke="${accent}" stroke-width="2"/><circle cx="0" cy="170" r="13" fill="${accent}"/></g><g font-family="Georgia, 'Cormorant Garamond', serif" font-weight="500" letter-spacing="22"><text text-anchor="middle" x="600" y="370" font-size="120" fill="${text}">COR<tspan fill="${accent}">A</tspan>VU</text></g><text text-anchor="middle" x="600" y="450" font-family="sans-serif" font-size="22" letter-spacing="6" fill="rgba(247,244,238,0.7)" font-weight="600">${tagline.toUpperCase()}</text></svg>`;
}

/* ─────────────────────── Registry ─────────────────────── */

export const LOGOS: Logo[] = [
  /* WORDMARK */
  {
    id: "wordmark-on-dark",
    name: "Primary Wordmark — On Dark",
    category: "Wordmark",
    background: "dark",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Default wordmark for dark chrome — marketing top nav, portal topbars, modal headers.",
    aspect: "wide",
    svg: wordmarkSvg({ text: "#F7F4EE", accent: "#B5873A" }),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-wordmark-on-dark" },
      { label: "PNG 1×", format: "png", pxSize: 320, filename: "coravu-wordmark-on-dark@1x" },
      { label: "PNG 2×", format: "png", pxSize: 640, filename: "coravu-wordmark-on-dark@2x" },
      { label: "PNG 3×", format: "png", pxSize: 960, filename: "coravu-wordmark-on-dark@3x" },
    ],
  },
  {
    id: "wordmark-on-light",
    name: "Primary Wordmark — On Light",
    category: "Wordmark",
    background: "light",
    usedIn: ["General", "Student"],
    description: "Wordmark for light surfaces — student portal sand background, marketing 'For Students' section.",
    aspect: "wide",
    svg: wordmarkSvg({ text: "#1C2B1A", accent: "#B5873A" }),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-wordmark-on-light" },
      { label: "PNG 1×", format: "png", pxSize: 320, filename: "coravu-wordmark-on-light@1x" },
      { label: "PNG 2×", format: "png", pxSize: 640, filename: "coravu-wordmark-on-light@2x" },
    ],
  },
  {
    id: "wordmark-reverse",
    name: "Reverse Wordmark — Amber",
    category: "Wordmark",
    background: "any",
    usedIn: ["General"],
    description: "All-amber treatment for muted contexts — footers, secondary surfaces.",
    aspect: "wide",
    svg: wordmarkSvg({ text: "#B5873A", accent: "#B5873A" }),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-wordmark-amber" },
      { label: "PNG 1×", format: "png", pxSize: 320, filename: "coravu-wordmark-amber@1x" },
    ],
  },
  {
    id: "lockup-on-dark",
    name: "Lockup — Mark + Wordmark",
    category: "Wordmark",
    background: "dark",
    usedIn: ["General", "Client", "Admin"],
    description: "Sun mark + wordmark lockup. Used in welcome emails, loading screens, RM intake.",
    aspect: "wide",
    svg: lockupSvg(),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-lockup" },
      { label: "PNG 2×", format: "png", pxSize: 800, filename: "coravu-lockup@2x" },
    ],
  },

  /* MARK */
  {
    id: "mark-on-dark",
    name: "Sun Mark — On Dark",
    category: "Mark",
    background: "dark",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Symbol-only mark for dark contexts — sidebar tiles, loading screens, OG fallback.",
    aspect: "square",
    svg: sunMarkSvg(),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-mark" },
      { label: "PNG 64", format: "png", pxSize: 64, filename: "coravu-mark-64" },
      { label: "PNG 128", format: "png", pxSize: 128, filename: "coravu-mark-128" },
      { label: "PNG 256", format: "png", pxSize: 256, filename: "coravu-mark-256" },
    ],
  },
  {
    id: "mark-on-light",
    name: "Sun Mark — On Light",
    category: "Mark",
    background: "light",
    usedIn: ["General", "Student"],
    description: "Same geometry, forest outer ring for visibility on sand/linen surfaces.",
    aspect: "square",
    svg: sunMarkSvg({ ringOuter: "#647F4D" }),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-mark-on-light" },
      { label: "PNG 64", format: "png", pxSize: 64, filename: "coravu-mark-on-light-64" },
      { label: "PNG 256", format: "png", pxSize: 256, filename: "coravu-mark-on-light-256" },
    ],
  },
  {
    id: "mark-mono-amber",
    name: "Sun Mark — Mono Amber",
    category: "Mark",
    background: "any",
    usedIn: ["General"],
    description: "Single-color amber mark for stamps, watermarks, single-color print.",
    aspect: "square",
    svg: sunMarkSvg({ ringOuter: "#B5873A", ringInner: "#B5873A", dot: "#B5873A" }),
    variants: [
      { label: "SVG", format: "svg", filename: "coravu-mark-mono-amber" },
      { label: "PNG 256", format: "png", pxSize: 256, filename: "coravu-mark-mono-amber-256" },
    ],
  },

  /* FAVICON */
  {
    id: "favicon-16",
    name: "Favicon 16×16",
    category: "Favicon",
    background: "transparent",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Browser tab icon — smallest variant. Lives at /favicons/favicon-16.png.",
    aspect: "square",
    svg: sunMarkSvg(),
    variants: [
      { label: "PNG 16", format: "png", pxSize: 16, filename: "favicon-16" },
      { label: "SVG", format: "svg", filename: "favicon" },
    ],
  },
  {
    id: "favicon-32",
    name: "Favicon 32×32",
    category: "Favicon",
    background: "transparent",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Standard browser tab icon. Bundled into /favicon.ico multi-size.",
    aspect: "square",
    svg: sunMarkSvg(),
    variants: [
      { label: "PNG 32", format: "png", pxSize: 32, filename: "favicon-32" },
      { label: "SVG", format: "svg", filename: "favicon-32" },
    ],
  },
  {
    id: "favicon-48",
    name: "Favicon 48×48",
    category: "Favicon",
    background: "transparent",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Windows pinned-tab favicon. Bundled into the multi-size .ico file.",
    aspect: "square",
    svg: sunMarkSvg(),
    variants: [
      { label: "PNG 48", format: "png", pxSize: 48, filename: "favicon-48" },
    ],
  },
  {
    id: "apple-touch",
    name: "Apple Touch Icon 180×180",
    category: "Favicon",
    background: "any",
    usedIn: ["General"],
    description: "iOS Add-to-Home-Screen icon. Lives at /favicons/apple-touch-icon.png.",
    aspect: "square",
    svg: sunMarkSvg({ bg: "#131e12" }),
    variants: [
      { label: "PNG 180", format: "png", pxSize: 180, filename: "apple-touch-icon" },
      { label: "SVG", format: "svg", filename: "apple-touch-icon" },
    ],
  },

  /* SOCIAL */
  {
    id: "og-default",
    name: "Open Graph Card — Default",
    category: "Social",
    background: "dark",
    usedIn: ["General"],
    description: "Default OG / Twitter card. 1200×630. Used by Slack, LinkedIn, Twitter, FB scrapers.",
    aspect: "social",
    svg: ogImageSvg(),
    variants: [
      { label: "SVG", format: "svg", filename: "og-default" },
      { label: "PNG 1200×630", format: "png", pxSize: 1200, filename: "og-default" },
    ],
  },
  {
    id: "og-pricing",
    name: "Open Graph Card — Pricing",
    category: "Social",
    background: "dark",
    usedIn: ["General"],
    description: "Variant for /pricing page sharing — same chrome, pricing tagline.",
    aspect: "social",
    svg: ogImageSvg({ tagline: "Programs that win you the role." }),
    variants: [
      { label: "SVG", format: "svg", filename: "og-pricing" },
      { label: "PNG 1200×630", format: "png", pxSize: 1200, filename: "og-pricing" },
    ],
  },

  /* APP ICON */
  {
    id: "app-icon-1024",
    name: "App Icon 1024×1024",
    category: "App Icon",
    background: "dark",
    usedIn: ["General"],
    description: "Master app icon for iOS / Android stores. Source for all derived sizes.",
    aspect: "square",
    svg: sunMarkSvg({ bg: "#131e12" }),
    variants: [
      { label: "PNG 1024", format: "png", pxSize: 1024, filename: "app-icon-1024" },
      { label: "SVG", format: "svg", filename: "app-icon" },
    ],
  },
  {
    id: "android-chrome-192",
    name: "Android Chrome 192×192",
    category: "App Icon",
    background: "any",
    usedIn: ["General"],
    description: "Web manifest icon, Android home-screen install. Referenced by /manifest.json.",
    aspect: "square",
    svg: sunMarkSvg({ bg: "#131e12" }),
    variants: [
      { label: "PNG 192", format: "png", pxSize: 192, filename: "android-chrome-192x192" },
    ],
  },
  {
    id: "android-chrome-512",
    name: "Android Chrome 512×512",
    category: "App Icon",
    background: "any",
    usedIn: ["General"],
    description: "Larger Android home-screen variant, splash screen fallback.",
    aspect: "square",
    svg: sunMarkSvg({ bg: "#131e12" }),
    variants: [
      { label: "PNG 512", format: "png", pxSize: 512, filename: "android-chrome-512x512" },
    ],
  },

  /* EMAIL */
  {
    id: "email-wordmark",
    name: "Email Wordmark",
    category: "Email",
    background: "light",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Transactional email header logo. Renders well in Gmail / Outlook / Apple Mail.",
    aspect: "wide",
    svg: wordmarkSvg({ text: "#1C2B1A", accent: "#B5873A" }),
    variants: [
      { label: "PNG 320", format: "png", pxSize: 320, filename: "email-wordmark" },
      { label: "PNG 640", format: "png", pxSize: 640, filename: "email-wordmark@2x" },
      { label: "SVG", format: "svg", filename: "email-wordmark" },
    ],
  },
  {
    id: "email-mark",
    name: "Email Sun Mark",
    category: "Email",
    background: "light",
    usedIn: ["General", "Student", "Client", "Admin"],
    description: "Compact email signature / sender avatar mark.",
    aspect: "square",
    svg: sunMarkSvg({ ringOuter: "#647F4D" }),
    variants: [
      { label: "PNG 128", format: "png", pxSize: 128, filename: "email-mark-128" },
      { label: "PNG 256", format: "png", pxSize: 256, filename: "email-mark-256" },
      { label: "SVG", format: "svg", filename: "email-mark" },
    ],
  },
];

export const LOGO_CATEGORIES: LogoCategory[] = [
  "Wordmark",
  "Mark",
  "Favicon",
  "Social",
  "App Icon",
  "Email",
];

/* ─────────────────────── Download helpers (web-only) ─────────────────────── */

export function downloadLogo(logo: Logo, variant: LogoVariant) {
  if (typeof window === "undefined") return;
  if (variant.format === "svg") {
    triggerBlobDownload(
      new Blob([logo.svg], { type: "image/svg+xml" }),
      `${variant.filename}.svg`,
    );
    return;
  }
  if (variant.format === "png" && variant.pxSize) {
    rasterizeAndDownload(logo.svg, variant.pxSize, `${variant.filename}.png`).catch(() => {
      // soft-fail
    });
  }
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke a tick so Safari completes the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function rasterizeAndDownload(svg: string, size: number, filename: string): Promise<void> {
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("svg load failed"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, size, size);
    await new Promise<void>((resolve) => {
      canvas.toBlob((pngBlob) => {
        if (pngBlob) triggerBlobDownload(pngBlob, filename);
        resolve();
      }, "image/png");
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
